import { useEffect, useState, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { mostrarToast } from '@/lib/formularioGuiado';
import Sheet from '@/components/ui/Sheet';
import Botao from '@/components/ui/Botao';
import type { Lead } from '../types';
import { formatarDataBR, formatarTelefone, telefoneParaWa } from '../utils';

interface FolhaLeadProps {
  lead: Lead | null;
  aberto: boolean;
  aoFechar: () => void;
  aoMudar?: () => void;
}

function Linha({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-background-200 py-3 last:border-b-0">
      <span className="shrink-0 text-sm text-foreground-500">{rotulo}</span>
      <span className="min-w-0 text-right text-sm font-medium text-foreground-950">{children}</span>
    </div>
  );
}

// Folha com todos os dados de um lead e as ações de contato.
export default function FolhaLead({ lead, aberto, aoFechar, aoMudar }: FolhaLeadProps) {
  const [descadastrado, setDescadastrado] = useState(false);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (aberto) setDescadastrado(false);
  }, [aberto, lead?.id]);

  if (!lead) return null;

  const wa = telefoneParaWa(lead.telefone);
  const temTelefone = wa.length > 0;

  async function copiarTelefone() {
    const texto = formatarTelefone(lead?.telefone);
    if (!texto) return;
    try {
      await navigator.clipboard.writeText(texto);
      mostrarToast('Telefone copiado.', 3000, 'sucesso');
    } catch {
      mostrarToast('Não foi possível copiar o telefone.', 3000, 'erro');
    }
  }

  async function alternarDescadastro(desfazer: boolean) {
    if (!lead) return;
    setSalvando(true);
    try {
      const { data, error } = await supabase.rpc('lead_descadastrar', {
        p_lead: lead.id,
        p_desfazer: desfazer,
      });
      if (error) throw error;
      const r = (data ?? {}) as { ok?: boolean };
      if (!r.ok) throw new Error('Não foi possível mudar agora.');
      setDescadastrado(!desfazer);
      mostrarToast(
        desfazer ? 'Voltou a receber mensagens.' : 'Não vai mais receber mensagens.',
        3500,
        desfazer ? 'sucesso' : 'neutro',
      );
      aoMudar?.();
    } catch (e) {
      mostrarToast(mensagemAmigavel(e), 4000, 'erro');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Sheet aberto={aberto} titulo={lead.nome || 'Lead'} aoFechar={aoFechar}>
      <div className="space-y-5">
        {/* Contato rápido */}
        <div className="flex flex-wrap gap-2">
          {temTelefone && (
            <a
              href={`https://wa.me/${wa}`}
              target="_blank"
              rel="noopener noreferrer"
              className="aperta inline-flex h-11 flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-[#25D366] px-5 text-sm font-semibold text-white transition-all hover:brightness-95"
            >
              <span className="flex h-5 w-5 items-center justify-center">
                <i className="ri-whatsapp-line" aria-hidden />
              </span>
              WhatsApp
            </a>
          )}
          {temTelefone && (
            <a
              href={`tel:+${wa}`}
              className="aperta inline-flex h-11 flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl border border-secondary-300 bg-white px-5 text-sm font-semibold text-foreground-900 transition-colors hover:bg-background-50"
            >
              <span className="flex h-5 w-5 items-center justify-center">
                <i className="ri-phone-line" aria-hidden />
              </span>
              Ligar
            </a>
          )}
          {temTelefone && (
            <Botao variante="secundario" icone="ri-file-copy-line" onClick={() => void copiarTelefone()}>
              Copiar telefone
            </Botao>
          )}
        </div>

        {/* Dados */}
        <div>
          <Linha rotulo="Telefone">
            {lead.telefone ? formatarTelefone(lead.telefone) : '—'}
          </Linha>
          <Linha rotulo="E-mail">{lead.email || '—'}</Linha>
          <Linha rotulo="Cidade">
            {lead.cidade ? `${lead.cidade}${lead.uf ? `/${lead.uf}` : ''}` : '—'}
          </Linha>
          <Linha rotulo="Tipo de pessoa">
            {lead.origens.length > 0 ? lead.origens.join(' · ') : '—'}
          </Linha>
          <Linha rotulo="Eventos">
            {lead.eventos.length > 0 ? lead.eventos.join(' · ') : '—'}
          </Linha>
          <Linha rotulo="Tem conta no app">{lead.tem_conta ? 'Sim' : 'Não'}</Linha>
          <Linha rotulo="Recebe avisos">{lead.recebe_avisos ? 'Sim' : 'Não'}</Linha>
          <Linha rotulo="Aceita WhatsApp">{lead.aceita_whatsapp ? 'Sim' : 'Não'}</Linha>
          <Linha rotulo="Primeiro contato">{formatarDataBR(lead.primeiro_em) || '—'}</Linha>
          <Linha rotulo="Último contato">{formatarDataBR(lead.ultimo_em) || '—'}</Linha>
        </div>

        {/* Descadastro */}
        {descadastrado ? (
          <div className="rounded-[16px] bg-background-100 p-3.5">
            <p className="text-sm text-foreground-700">Não vai mais receber mensagens.</p>
            <button
              type="button"
              onClick={() => void alternarDescadastro(true)}
              disabled={salvando}
              className="aperta mt-1.5 cursor-pointer text-sm font-semibold text-primary-700 hover:underline disabled:opacity-60"
            >
              Desfazer
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => void alternarDescadastro(false)}
            disabled={salvando}
            className="aperta flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl border border-secondary-300 bg-white px-5 py-3 text-sm font-semibold text-danger-600 transition-colors hover:bg-danger-50 disabled:opacity-60"
          >
            <span className="flex h-5 w-5 items-center justify-center">
              {salvando ? (
                <i className="ri-loader-4-line animate-spin" aria-hidden />
              ) : (
                <i className="ri-notification-off-line" aria-hidden />
              )}
            </span>
            Não quer receber mensagens
          </button>
        )}
      </div>
    </Sheet>
  );
}